import { cn, Loader, RadioGroup, RadioGroupProps, Stack } from 'component-library';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { PlotReportFactLabelFilter, R1FactType } from '@/api/rest/resources/types/fact';
import { PlotGroup } from '@/api/rest/resources/types/plotGroup';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { ChartZoomStateHook } from '@/components/Charts/components/ChartZoom';
import { GRAPH_TYPE, GraphTypeValue } from '@/components/Charts/hooks/useChartType';
import {
  GraphFactsToPlotsMap,
  useFactTypeForItemizedGraph,
  useGraphFactsToPlotsMap,
  useIndicatorFactsMap,
} from '@/components/Charts/hooks/useGraphFactsToPlotsMap';
import {
  usePlotCountLimit,
  usePlotsForItemizedGraph,
  UsePlotsForItemizedGraphParams,
} from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { ItemizedBarChart, ScalarDataPoint } from '@/components/Charts/ItemizedBarChart';
import { ItemizedTimeseriesChart, Timeseries } from '@/components/Charts/ItemizedTimeseriesChart';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { useActivePlotGroup, usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { parsePlotGroupBodyData } from '@/components/PlotGroups/utils/parsePlotGroupBodyData';
import { withSuspenseBoundary } from '@/components/SuspenseBoundary';
import { RadioItem } from '@/components/widgets/a/RadioItem/RadioItem';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { useOrganizationPlotGroup } from '@/pages/shared/hooks/useOrganizationPlotGroup';
import { isNullish } from '@/utils/booleans';
import { formatUnit } from '@/utils/formatting';

import { useAggregationBenchmarks, useBenchmarksForAggregation } from '../hooks/useAggregationBenchmarks';

const INDICATORS_WITH_NO_BENCHMARKS = [
  GraphTileType.social_life_expectancy,
  GraphTileType.historic_habitat_intactness,
  GraphTileType.historic_habitat_intactness_threats_breakdown,
  GraphTileType.historic_above_ground_carbon,
];

// TODO: MVP-7389 remove when groupby filters create plotgroups of their own
const useShowItemizedGraphType = () => {
  const activePlotGroup = useActivePlotGroup();

  const organizationId = useOrganizationId();
  const plotGroupId = usePlotGroupId();
  const parentPlotGroup = useOrganizationPlotGroup(organizationId, plotGroupId);

  if (isNullish(activePlotGroup) && isNullish(parentPlotGroup)) {
    return true;
  }

  const groupHasGrouping = (group: PlotGroup) => {
    return !isNullish(group)
      ? group.groupBy && Object.keys(group.groupBy).length > 0 && Object.values(group.groupBy).some((v) => v !== null)
      : false;
  };

  const activeGroupHasGrouping = groupHasGrouping(activePlotGroup);
  const parentGroupHasGrouping = !isNullish(parentPlotGroup)
    ? groupHasGrouping(parsePlotGroupBodyData(parentPlotGroup))
    : false;

  return !activeGroupHasGrouping && !parentGroupHasGrouping;
};

type ItemizedGraphTypeProps = {
  className?: string;
  indicator?: GraphTileType;
  onChange?: (value: GraphTypeValue) => void;
} & Omit<RadioGroupProps, 'onChange'>;

export const ItemizedGraphType = ({ className, value, onChange, indicator, ...delegated }: ItemizedGraphTypeProps) => {
  const { setAnalysisType, analysisType } = useNCCardContext();
  const enableRadioButtons = useShowItemizedGraphType();

  const { t } = useTranslation();
  const indicatorFactsMap = useIndicatorFactsMap();

  const handleOnValueChange = (newValue: string | boolean | null | undefined) => {
    if (typeof newValue !== 'string') return;

    /**
     * Set default analysis type to 'average' if available for the selected graph type and indicator.
     * This allows a better comparison across plots in aggregation bar charts.
     */
    if (indicator) {
      const averageFact = indicatorFactsMap[`${newValue as GraphTypeValue}`]?.[`${indicator}`]?.average;

      if (averageFact && analysisType !== 'average') {
        setAnalysisType('average');
      }
    }

    onChange?.(newValue as GraphTypeValue);
  };

  return (
    <RadioGroup
      onValueChange={handleOnValueChange}
      data-testid='graph-type-radio-buttons'
      onClick={(e) => e.stopPropagation()}
      className={cn('gap-0', className)}
      disabled={!enableRadioButtons}
      {...delegated}
    >
      <RadioItem
        label={t('graph.chartStyle.radioLabel.title.default')}
        value={GRAPH_TYPE.DEFAULT}
        isChecked={value === GRAPH_TYPE.DEFAULT}
        dataTestid='default-option'
      />
      <RadioItem
        label={t('graph.chartStyle.radioLabel.title.line')}
        value={GRAPH_TYPE.LINE}
        isChecked={value === GRAPH_TYPE.LINE}
        dataTestid='line-option'
      />
      <RadioItem
        label={t('graph.chartStyle.radioLabel.title.bar')}
        value={GRAPH_TYPE.BAR}
        isChecked={value === GRAPH_TYPE.BAR}
        dataTestid='bar-option'
      />
    </RadioGroup>
  );
};

type ItemizedGraphTypeRendererProps = {
  graphType: GraphTypeValue | undefined;
  defaultGraphType: ReactElement;
  indicator: GraphTileType;
  labels?: PlotReportFactLabelFilter;
  customBenchmark?: CustomBenchmarkSuccessResponse | null;
} & UsePlotsForItemizedGraphParams &
  Partial<ChartZoomStateHook>;

export const ItemizedGraphTypeRenderer = ({
  graphType,
  defaultGraphType,
  indicator,
  plotRenderType,
  plotGroupId,
  zoom,
  setZoom,
  customBenchmark,
  labels,
}: ItemizedGraphTypeRendererProps) => {
  const showToggles = useShowItemizedGraphType();
  if (!graphType || graphType === GRAPH_TYPE.DEFAULT || !showToggles) {
    return defaultGraphType;
  }

  return (
    <ItemizedGraphsRenderer
      indicator={indicator}
      plotRenderType={plotRenderType}
      graphType={graphType}
      plotGroupId={plotGroupId}
      zoom={zoom}
      setZoom={setZoom}
      labels={labels}
      customBenchmark={customBenchmark}
    />
  );
};

const ItemizedGraphsRenderer = withSuspenseBoundary(
  ({
    graphType,
    indicator,
    plotRenderType,
    plotGroupId,
    zoom,
    setZoom,
    labels,
    customBenchmark,
  }: Omit<ItemizedGraphTypeRendererProps, 'defaultGraphType' | 'graphType'> & {
    graphType: GraphTypeValue;
  }) => {
    return (
      <ItemizedGraphs
        indicator={indicator}
        plotRenderType={plotRenderType}
        graphType={graphType}
        plotGroupId={plotGroupId}
        zoom={zoom}
        setZoom={setZoom}
        labels={labels}
        customBenchmark={customBenchmark}
      />
    );
  },
  <Stack className='h-full w-full' centerMain>
    <Loader className='min-h-[300px]' />
  </Stack>,
);

const ItemizedGraphs = ({
  graphType,
  indicator,
  plotRenderType,
  plotGroupId,
  zoom,
  setZoom,
  labels,
  customBenchmark,
}: Omit<ItemizedGraphTypeRendererProps, 'defaultGraphType' | 'graphType'> & {
  graphType: GraphTypeValue;
}) => {
  const { t } = useTranslation();
  const { isPlotCountLimited } = usePlotCountLimit({ plotRenderType, plotGroupId });
  const aggregationPlots = usePlotsForItemizedGraph({ plotRenderType, plotGroupId });
  const plotsFacts = useGraphFactsToPlotsMap({ aggregationPlots, indicator, graphType, plotGroupId, labels });
  const factType = useFactTypeForItemizedGraph({ indicator, graphType });
  const indicatorYAxisLabels = {
    [GraphTileType.ndvi]: t('shared.ncaDetail.details.ndvi.labels.ndvi'),
  };

  const yAxisLabel = indicatorYAxisLabels[indicator as keyof typeof indicatorYAxisLabels] ?? undefined;

  if (!factType) {
    return null;
  }

  const graphUnit = getGraphUnits({ plotsFacts, factType });

  if (INDICATORS_WITH_NO_BENCHMARKS.includes(indicator)) {
    return (
      <ItemizedGraphsWithoutBenchmarks
        graphType={graphType}
        indicator={indicator}
        plotsFacts={plotsFacts}
        factType={factType}
        unit={graphUnit}
        zoom={zoom}
        setZoom={setZoom}
        isPlotCountLimited={isPlotCountLimited}
        yAxisLabel={yAxisLabel}
      />
    );
  }

  return (
    <ItemizedGraphsWithBenchmarks
      graphType={graphType}
      indicator={indicator}
      plotsFacts={plotsFacts}
      plotGroupId={plotGroupId}
      factType={factType}
      unit={graphUnit}
      zoom={zoom}
      setZoom={setZoom}
      isPlotCountLimited={isPlotCountLimited}
      yAxisLabel={yAxisLabel}
      customBenchmark={customBenchmark}
    />
  );
};

type ItemizedGraphsWithBenchmarksProps = Omit<
  ItemizedGraphTypeRendererProps,
  'defaultGraphType' | 'graphType' | 'plotRenderType'
> & {
  graphType: GraphTypeValue;
  plotsFacts: GraphFactsToPlotsMap;
  factType: R1FactType;
  unit: UnitEnum;
  isPlotCountLimited: boolean;
  yAxisLabel?: string;
};

const ItemizedGraphsWithBenchmarks = ({
  graphType,
  indicator,
  plotsFacts,
  plotGroupId,
  factType,
  unit,
  zoom,
  setZoom,
  isPlotCountLimited,
  yAxisLabel,
  customBenchmark,
}: ItemizedGraphsWithBenchmarksProps) => {
  const benchmarks = useAggregationBenchmarks({ indicator, plotGroupId });
  const aggregationBenchmarks = useBenchmarksForAggregation({ indicator, plotGroupId });

  if (graphType === GRAPH_TYPE.LINE) {
    const graphData = getGraphData({ plotsFacts, factType });
    return (
      <ItemizedTimeseriesChart
        data={graphData as Timeseries[]}
        indicator={indicator}
        unit={unit}
        benchmarks={benchmarks}
        zoom={zoom}
        setZoom={setZoom}
        isPlotCountLimited={isPlotCountLimited}
        yAxisLabel={yAxisLabel}
        customBenchmark={customBenchmark}
      />
    );
  }

  if (graphType === GRAPH_TYPE.BAR) {
    const graphData = getGraphData({ plotsFacts, factType });
    return (
      <ItemizedBarChart
        data={graphData as ScalarDataPoint[]}
        indicator={indicator}
        unit={unit}
        benchmarks={benchmarks}
        aggregationBenchmarks={aggregationBenchmarks}
        isPlotCountLimited={isPlotCountLimited}
        yAxisLabel={yAxisLabel}
        customBenchmark={customBenchmark}
      />
    );
  }
  return null;
};

const ItemizedGraphsWithoutBenchmarks = ({
  graphType,
  indicator,
  plotsFacts,
  factType,
  unit,
  zoom,
  setZoom,
  isPlotCountLimited,
  yAxisLabel,
}: Omit<ItemizedGraphTypeRendererProps, 'defaultGraphType' | 'graphType' | 'plotRenderType'> & {
  graphType: GraphTypeValue;
  plotsFacts: GraphFactsToPlotsMap;
  factType: R1FactType;
  unit: UnitEnum;
  isPlotCountLimited: boolean;
  yAxisLabel?: string;
}) => {
  if (graphType === GRAPH_TYPE.LINE) {
    const graphData = getGraphData({ plotsFacts, factType }) as Timeseries[];
    return (
      <ItemizedTimeseriesChart
        data={graphData}
        indicator={indicator}
        unit={unit}
        zoom={zoom}
        setZoom={setZoom}
        isPlotCountLimited={isPlotCountLimited}
        yAxisLabel={yAxisLabel}
      />
    );
  }

  if (graphType === GRAPH_TYPE.BAR) {
    const graphData = getGraphData({ plotsFacts, factType }) as ScalarDataPoint[];
    return (
      <ItemizedBarChart
        data={graphData}
        indicator={indicator}
        unit={unit}
        isPlotCountLimited={isPlotCountLimited}
        yAxisLabel={yAxisLabel}
      />
    );
  }
  return null;
};

export const getGraphUnits = ({ plotsFacts, factType }: { plotsFacts: GraphFactsToPlotsMap; factType: R1FactType }) => {
  const fact = plotsFacts.flatMap((plotFact) => plotFact.facts).find((f) => f.name === factType);

  if (!fact) {
    return UnitEnum.none;
  }

  const factValue = Array.isArray(fact?.value) ? fact?.value[0] : fact;
  const factUnit = factValue?.unit ?? UnitEnum.none;

  return formatUnit(factUnit) as UnitEnum;
};

const getGraphData = ({ plotsFacts, factType }: { plotsFacts: GraphFactsToPlotsMap; factType: R1FactType }) => {
  return plotsFacts.flatMap(({ plot, facts }) => {
    const targetFact = facts.find((fact) => fact.name === factType);

    if (!targetFact || isNullish(targetFact.value)) {
      return [];
    }

    return {
      id: plot.id,
      name: plot.name,
      value: targetFact?.value as unknown,
      unit: targetFact?.unit,
    };
  });
};
